import {useMemo} from 'react';
import {
    autoUpdate,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useInteractions,
    useRole,
    useTransitionStyles,
} from '@floating-ui/react';

import {zIndexStore} from '@pb/kit-component-floating';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';

import type {UseDismissProps, UseRoleProps} from '@floating-ui/react';

type UseDialogFloatingProps = {
    isAnchor: boolean,
    dismissProps?: UseDismissProps,
    roleProps?: UseRoleProps,
    shown?: boolean,
    setShown?: (shown: boolean) => void,
};
export const useDialogFloating = ({
    shown,
    setShown,
    isAnchor,
    dismissProps,
    roleProps,
}: UseDialogFloatingProps) => {
    const [open, setOpen] = useControlledState(shown, setShown, false);
    const {context, floatingStyles, refs} = useFloating({
        strategy: 'fixed',
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
    });
    const nodeId = useFloatingNodeId();
    const {isMounted, styles} = useTransitionStyles(context, {
        duration: 300,
        initial: {opacity: 0},
        open: {opacity: 1},
        close: {opacity: 0},
    });
    const click = useClick(context, {
        enabled: isAnchor,
    });
    const dismiss = useDismiss(context, {
        outsidePress: true,
        ...dismissProps,
    });
    const role = useRole(context, {
        ...roleProps,
        enabled: !!roleProps?.enabled,
    });
    const {getFloatingProps, getReferenceProps} = useInteractions([dismiss, click, role]);

    const zIndex = useMemo(() => {
        if (open) {
            return zIndexStore.next();
        }

        return undefined;
    }, [open]);

    return {
        open,
        nodeId,
        floatingStyles,
        refs,
        zIndex,
        context,
        getReferenceProps,
        getFloatingProps,
        isMounted,
        styles,
    };
};
